/** @jsxImportSource @emotion/react */
/* eslint-disable @next/next/no-img-element */
/**
 * BookingLocationDetailsModal.js
 * Day-view arm modal ("Where would you like your service done?"). Collects the final address parts
 * (location type, apt, additional instructions). The address itself is an editable Places search when
 * none exists yet, and locked/read-only when one was already chosen upstream. No pets.
 *
 * Typography and controls reuse the shared library (Header2, Text, Button, SignInText); the styled
 * CSS here is limited to layout containers, the underline inputs, and the location-type chips (the
 * codebase's established container-scoped button pattern).
 */
import React, { useState } from 'react'
import { css } from '@emotion/react'
import Modal from './Modal'
import AddressAutocomplete from './AddressAutocomplete'
import SignInText from '../../../sign-up-sign-in-form/SignInText'
import { Header2, Text, Button } from '../../../../styles/Components'
import { gsColors as c } from '../../../../styles/Variables'
import { mq } from '../../../../helpers'

const LOCATION_TYPES = ['Home', 'Office', 'Hotel', 'Other']

// Font sizes are pinned per breakpoint here rather than via the shared Text/Header sizing props,
// which scale on mobile (Text multiplies by 0.75 under 768px; Header2 hard-locks mobile to 25px).
// !important is needed because the components' own font-size rules are equal specificity.
const wrapCss = css`
  max-width: 624px;
  margin: 0 auto;

  h2 { font-size: 48px !important; }
  .subtitle { font-size: 18px !important; }
  .type-label { font-size: 18px !important; }
  .instructions-link { font-size: 18px !important; }
  .cta { font-size: 18px !important; }

  ${mq('max', '768px')} {
    max-width: none;
    margin: 0;
    padding: 10px 20px 24px;

    h2 { font-size: 28px !important; }
    .subtitle { font-size: 16px !important; }
    .type-label { font-size: 14px !important; }
    .instructions-link { font-size: 14px !important; }
    .cta { font-size: 16px !important; }
  }
`
const lockedAddressCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  border-bottom: 1px solid ${c.black};
  color: ${c.black};
  font-family: Euclid;
  font-size: 18px;
  img {
    width: 22px;
    height: auto;
  }
`
const addressWrapCss = css`
  & > div {
    padding: 0;
    margin: 0;
    max-width: none;
  }
  & .places-autocomplete::placeholder {
    color: ${c.mediumGrey} !important;
  }
`
const aptInputCss = css`
  width: 100%;
  margin-top: 12px;
  border: 0;
  border-bottom: 1px solid ${c.black};
  background: transparent;
  font-family: Euclid;
  font-size: 18px;
  color: ${c.black};
  padding: 12px 0;
  &::placeholder {
    color: ${c.mediumGrey};
  }
  &:focus {
    outline: none;
  }
`
const typeBlockCss = css`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px 0;
`
const optionRowCss = css`display: flex; gap: 16px;`
const optionCss = css`
  flex: 1 0 0;
  min-width: 0;
  max-width: 88px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${c.white};
  border: 1px solid ${c.darkGrey};
  border-radius: 3px;
  color: ${c.black};
  font-family: Euclid;
  font-size: 18px;
  font-weight: 400;
  line-height: 1;
  text-transform: capitalize;
  white-space: nowrap;
  cursor: pointer;
  &.active {
    background: ${c.black};
    border-color: ${c.black};
    border-radius: 4px;
    color: ${c.white};
  }
  ${mq('max', '768px')} {
    max-width: none;
    height: 42px;
    font-size: 14px;
  }
`
const linkBtnCss = css`
  display: inline-flex;
  align-items: center;
  background: none;
  border: 0;
  padding: 12px 0;
  cursor: pointer;
`
const notesCss = css`
  width: 100%;
  border: 1px solid ${c.paleSlate};
  border-radius: 3px;
  padding: 12px;
  font-family: Brown;
  resize: none;
  margin-bottom: 8px;
`

/**
 * BookingLocationDetailsModal
 * Day-view booking arm modal that collects the final address details (location type, apartment,
 * additional instructions) before moving to date/time selection. When `address` already has a zip,
 * the address is rendered read-only; otherwise an AddressAutocomplete lets the user search for one.
 * Calls `onSubmit(effectiveMarket, effectiveAddress)` where effectiveAddress merges the captured or
 * existing address with the collected type/apartment/notes.
 */
const BookingLocationDetailsModal = ({
  isOpen, close, isDesktop, currentMarket, address = {}, market,
  serviceRequests = [], allMarketServices = [], onSubmit, onSignIn,
}) => {
  const hasAddress = !!address.zip
  const [capturedAddress, setCapturedAddress] = useState()
  const [capturedMarket, setCapturedMarket] = useState()
  const [type, setType] = useState(address.type || '')
  const [apartment, setApartment] = useState(address.apartment || '')
  const [notes, setNotes] = useState(address.notes || '')
  const [showInstructions, setShowInstructions] = useState(!!address.notes)

  const effectiveAddress = hasAddress ? address : capturedAddress
  const effectiveMarket = hasAddress ? market : capturedMarket
  const isValid = !!(effectiveAddress && effectiveAddress.zip) && !!type

  const submit = () => onSubmit(effectiveMarket, { ...effectiveAddress, type, apartment, notes })

  return (
    <Modal
      isOpen={isOpen}
      closeModal={close}
      width="838px"
      padding={isDesktop ? '51px 0' : undefined}
      isDesktop={isDesktop}
    >
      <div css={wrapCss}>
        <Header2 align="center" margin="0 0 16px">Where would you like your service done?</Header2>
        <Text className="subtitle" color={c.darkGrey} textAlign="center" lineHeight="138%" margin="0 0 24px">
          You&apos;ll select a date and time next.{' '}
          {isDesktop && <SignInText inline isDesktop={isDesktop} onRequestOpen={onSignIn}>{'Booked with us before? '}</SignInText>}
        </Text>

        {hasAddress ? (
          <div css={lockedAddressCss}>
            <img src="/images/new-location-icon.svg" alt="" aria-hidden="true" />
            {address.formatted || address.street}
          </div>
        ) : (
          <div css={addressWrapCss}>
            <AddressAutocomplete
              currentMarket={currentMarket}
              hasSelectedServices
              serviceRequests={serviceRequests}
              allMarketServices={allMarketServices}
              dispatch={({ market: m, address: a }) => { setCapturedMarket(m); setCapturedAddress(a) }}
            />
          </div>
        )}

        <input
          css={aptInputCss}
          placeholder="Apt, unit, room number"
          value={apartment}
          onChange={e => setApartment(e.target.value)}
        />

        <div css={typeBlockCss}>
          <Text className="type-label" weight={500}>What type of location is this?</Text>
          <div css={optionRowCss}>
            {LOCATION_TYPES.map(name => (
              <button
                key={name}
                type="button"
                aria-label={name}
                aria-pressed={type === name}
                className={type === name ? 'active' : undefined}
                css={optionCss}
                onClick={() => setType(name)}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        {showInstructions ? (
          <textarea
            css={notesCss}
            rows={3}
            placeholder="Building security? Hard to find? Tell us here."
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        ) : (
          <button type="button" css={linkBtnCss} onClick={() => setShowInstructions(true)}>
            <Text as="span" className="instructions-link" color={c.cobalt} weight={600}>+ Add additional instructions</Text>
          </button>
        )}

        <Button
          fill
          large
          className="cta"
          margin="24px 0 0"
          disabled={!isValid}
          color={isValid ? c.sunsetOrange : c.greyPoupon}
          onClick={submit}
        >
          Continue To Date &amp; Time
        </Button>

        {!isDesktop && (
          <SignInText isDesktop={isDesktop}>{'Booked with Glamsquad before? '}</SignInText>
        )}
      </div>
    </Modal>
  )
}

export default BookingLocationDetailsModal
